// ===============================
// AXOIX Pricing Data
// All pricing tiers, addons, and features
// ===============================

export interface PricingFeature {
  text: string;
  included: boolean;
  highlight?: boolean;
}

export interface PricingTier {
  id: string;
  name: string;
  shipName: string;
  description: string;
  // Discounted prices (what customer pays)
  monthlyPrice: number | null;
  quarterlyPrice: number | null;
  sixMonthPrice: number | null;
  yearlyPrice: number | null;
  fiveYearPrice: number | null;
  tenYearPrice: number | null;
  // Original prices (before introductory discount - shown crossed out)
  originalMonthlyPrice: number | null;
  originalQuarterlyPrice: number | null;
  originalSixMonthPrice: number | null;
  originalYearlyPrice: number | null;
  originalFiveYearPrice: number | null;
  originalTenYearPrice: number | null;
  // Discount percentage for this tier
  discountPercent: number;
  currency: string;
  popular?: boolean;
  features: PricingFeature[];
  ctaText: string;
  ctaLink: string;
  badge?: string;
  comingSoon?: boolean;
}

// ===============================
// SPECIALTY ADDON (Tender Management Only)
// Only paid addon - rest are included features
// ===============================
export interface SpecialtyAddon {
  id: string;
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  currency: string;
  industries: string[]; // Which industries benefit most
  features: string[];
  character: string; // AI character that handles this
  icon: string;
  badge?: string;
  comingSoon?: boolean;
}

export const SPECIALTY_ADDONS: SpecialtyAddon[] = [
  {
    id: 'tender_management',
    name: 'Tender Management',
    description: 'Complete NIT extraction, CPWD rates, tender quotes, and auto-project creation for government contractors.',
    monthlyPrice: 1500,
    yearlyPrice: 12999,
    currency: 'INR',
    industries: ['gov_contractor', 'construction'],
    features: [
      'NIT Extractor with 67+ fields',
      'CPWD DSR Rate Database',
      'BOQ-based Tender Quotes',
      'Auto-Project Creation on winning',
      'RA Bills & Measurement Book',
      'EMD/SD Tracking',
      'Tender deadline alerts',
    ],
    character: 'PROBE',
    icon: 'fas fa-file-contract',
    badge: 'Coming Soon',
    comingSoon: true,
  },
  {
    id: 'link_phone_system',
    name: 'LINK - Business Phone System',
    description: 'Get a real phone number (DID), professional IVR, call recording, and a connection to the public phone network — for businesses that need to take external calls.',
    monthlyPrice: 2500,
    yearlyPrice: 24999,
    currency: 'INR',
    industries: ['all'],
    features: [
      '1 DID number (+91-XXX-XXXX)',
      'Professional IVR builder',
      'Call recording & storage',
      'Click-to-call from CRM',
      'Missed call alerts & analytics',
    ],
    character: 'LINK',
    icon: 'fas fa-phone-volume',
    badge: 'Coming Soon',
    comingSoon: true,
  },
];

// ===============================
// INCLUDED FEATURES (Free with Plans)
// Email, DocuSign, Complaint Management - included, not paid
// ===============================
export interface IncludedFeature {
  id: string;
  name: string;
  description: string;
  features: string[];
  character: string;
  icon: string;
  availableFrom: 'visitor' | 'crew' | 'officer' | 'commander';
  comingSoon?: boolean;
}

export const INCLUDED_FEATURES: IncludedFeature[] = [
  {
    id: 'email_suite',
    name: 'Email Suite',
    description: 'Professional email setup with custom domain, templates, and automated workflows.',
    features: [
      'Custom domain email setup',
      'Professional email templates',
      'Automated email workflows',
      'Email tracking & analytics',
      'Integration with all modules',
    ],
    character: 'ECHO',
    icon: 'fas fa-envelope',
    availableFrom: 'crew',
  },
  {
    id: 'seal',
    name: 'SEAL - E-Signature Module',
    description: 'Secure Electronic Agreement & Ledger. Digital signatures for quotes, contracts, and important documents. FREE for all users including Explorer plan.',
    features: [
      'E-signatures on quotes & proposals',
      'Contract signing workflow',
      'Document sharing & tracking',
      'Legal compliance (IT Act 2000)',
      'Audit trail for signatures',
    ],
    character: 'SEAL',
    icon: 'fas fa-stamp',
    availableFrom: 'visitor',
  },
  {
    id: 'remedy',
    name: 'REMEDY - Complaint Management',
    description: 'Resolution & Mediation for Your Disputes. Track, manage, and resolve customer complaints with SLA monitoring and escalation. FREE for all users including Explorer plan.',
    features: [
      'Complaint ticket system',
      'SLA monitoring & alerts',
      'Escalation workflows',
      'Customer communication log',
      'Resolution tracking',
    ],
    character: 'REMEDY',
    icon: 'fas fa-heartbeat',
    availableFrom: 'visitor',
  },
];

// ===============================
// VERTICAL/INDUSTRY PRICING
// Additional industries beyond the first included vertical
// ===============================
// REMOVED 2026-09-08 — VERTICAL_PRICING_CONFIG and getVerticalPrice().
//
// These held a Rs 10,000/mo list price with a 60% "signup discount", and
// getVerticalPrice(true) returned 10000 x 0.40 = Rs 4,000. That happened to
// equal the API's real price, but only by arithmetic coincidence: nothing
// kept the two in step, and getVerticalPrice(false) returned Rs 5,000, which
// matched nothing at all. Signup and checkout used it to compute the CART
// TOTAL while the Mothership prices the real order from `pricing_tiers`, so
// a change on either side would have shown the customer one number and
// charged another.
//
// Vertical prices now come from `/api/pricing/verticals` via
// `hooks/useVerticalPricing.ts`. There is no list price and no signup
// discount in the API, so the "60% off at signup!" label went with it —
// a discount claim we could not keep true across a price change.

// AVAILABLE_VERTICALS, getVerticalPrice() and TIER_VERTICAL_LIMITS removed
// 2026-09-08 together with VERTICAL_PRICING_CONFIG above.
//
// AVAILABLE_VERTICALS listed ten verticals at Rs 10,000 while the platform
// has nineteen (app/core/verticals_canonical.py) — it was both stale and
// incomplete, and no component imported it. Signup builds its own list.
//
// TIER_VERTICAL_LIMITS duplicated `pricing_tiers.feature_limits.verticals`,
// which is what tier_limits.py actually enforces. Signup now reads that via
// useVerticalPricing(), so the picker cannot offer a vertical the server
// would refuse.

export const PRICING_TIERS: PricingTier[] = [
  {
    id: 'explorer',
    name: 'Explorer',
    shipName: 'Explorer Vessel',
    description: 'Explore AXOIX and discover what AI-powered business management can do.',
    // Discounted prices (FREE - no discount applicable)
    monthlyPrice: 0,
    quarterlyPrice: 0,
    sixMonthPrice: 0,
    yearlyPrice: 0,
    fiveYearPrice: null,
    tenYearPrice: null,
    // Original prices (same as discounted for free plan)
    originalMonthlyPrice: 0,
    originalQuarterlyPrice: 0,
    originalSixMonthPrice: 0,
    originalYearlyPrice: 0,
    originalFiveYearPrice: null,
    originalTenYearPrice: null,
    discountPercent: 0,
    currency: 'INR',
    features: [
      { text: 'Basic dashboard access', included: true },
      { text: 'AXOIX AI assistant (limited)', included: true },
      { text: 'SEAL E-Signatures', included: true },
      { text: 'REMEDY Complaints', included: true },
      { text: 'Single language support', included: true },
      { text: 'Community support only', included: true },
      { text: 'Standard analytics view', included: true },
      { text: 'Full AI character access', included: false },
      { text: 'Voice commands', included: false },
      { text: 'Multi-language support', included: false },
    ],
    ctaText: 'Start Exploring',
    ctaLink: '/signup',
    badge: 'Free Forever',
  },
  {
    id: 'officer',
    name: 'Officer',
    shipName: 'Officer Cruiser',
    description: 'Command your division with enhanced capabilities and priority support.',
    // Standard pricing
    monthlyPrice: 1500,
    quarterlyPrice: 4275,      // 5% off 3x monthly
    sixMonthPrice: 8100,       // 10% off 6x monthly
    yearlyPrice: 14400,        // 20% off 12x monthly
    fiveYearPrice: null,
    tenYearPrice: null,
    originalMonthlyPrice: null,
    originalQuarterlyPrice: null,
    originalSixMonthPrice: null,
    originalYearlyPrice: null,
    originalFiveYearPrice: null,
    originalTenYearPrice: null,
    discountPercent: 0,
    currency: 'INR',
    popular: true,
    comingSoon: false,
    features: [
      { text: 'Everything in Free +', included: true, highlight: true },
      { text: '20 employees, 2 companies', included: true },
      { text: 'Up to 2 industry verticals', included: true },
      { text: 'Full 22 AI character access', included: true },
      { text: 'Voice control in 81 languages', included: true },
      { text: 'Multi-language (81 languages)', included: true },
      { text: 'Email Suite (5 accounts)', included: true },
      { text: '25 GB storage', included: true },
      { text: 'Storefront themes unlocked', included: true },
      { text: 'Email support', included: true },
    ],
    ctaText: 'Get Started',
    ctaLink: '/signup?tier=officer',
    badge: undefined,
  },
  {
    id: 'commander',
    name: 'Commander',
    shipName: 'Command Battleship',
    description: 'Scale your operations with 3x resources and priority support.',
    // Standard pricing
    monthlyPrice: 3600,
    quarterlyPrice: 10260,     // 5% off 3x monthly
    sixMonthPrice: 19440,      // 10% off 6x monthly
    yearlyPrice: 34560,        // 20% off 12x monthly
    fiveYearPrice: null,
    tenYearPrice: null,
    originalMonthlyPrice: null,
    originalQuarterlyPrice: null,
    originalSixMonthPrice: null,
    originalYearlyPrice: null,
    originalFiveYearPrice: null,
    originalTenYearPrice: null,
    discountPercent: 0,
    currency: 'INR',
    comingSoon: false,
    features: [
      { text: 'Everything in Officer +', included: true, highlight: true },
      { text: '50 employees, 6 companies', included: true },
      { text: 'Up to 5 industry verticals', included: true },
      { text: 'All storefront themes unlocked', included: true },
      { text: '50 GB storage', included: true },
      { text: 'Email Suite (20 accounts)', included: true },
      { text: 'AI Costing: 100 calls/week', included: true },
      { text: 'Priority support', included: true },
      { text: 'Advanced analytics', included: true },
      { text: 'API access', included: true },
    ],
    ctaText: 'Get Started',
    ctaLink: '/signup?tier=commander',
    badge: undefined,
  },
  {
    id: 'titan',
    name: 'Titan',
    shipName: 'Titan Mothership',
    description: 'Enterprise-grade with 5x resources, all addons included, and dedicated support.',
    // Standard pricing
    monthlyPrice: 7500,
    quarterlyPrice: 21375,     // 5% off 3x monthly
    sixMonthPrice: 40500,      // 10% off 6x monthly
    yearlyPrice: 72000,        // 20% off 12x monthly
    fiveYearPrice: null,
    tenYearPrice: null,
    originalMonthlyPrice: null,
    originalQuarterlyPrice: null,
    originalSixMonthPrice: null,
    originalYearlyPrice: null,
    originalFiveYearPrice: null,
    originalTenYearPrice: null,
    discountPercent: 0,
    currency: 'INR',
    comingSoon: false,
    features: [
      { text: 'Everything in Commander +', included: true, highlight: true },
      { text: '100 employees, 10 companies', included: true },
      { text: 'Up to 10 industry verticals', included: true },
      { text: 'ALL Specialty Addons included', included: true, highlight: true },
      { text: '200 GB storage', included: true },
      { text: 'Email Suite (50 accounts)', included: true },
      { text: 'AI Costing: 300 calls/week', included: true },
      { text: 'Unlimited API calls', included: true },
      { text: 'White-label options', included: true },
      { text: 'Custom AI training', included: true },
      { text: 'Dedicated account manager', included: true },
      { text: 'Phone + Priority support', included: true },
    ],
    ctaText: 'Get Started',
    ctaLink: '/signup?tier=titan',
    badge: undefined,
  },
];

// Feature comparison data for table
export interface FeatureCategory {
  name: string;
  features: {
    name: string;
    explorer: string | boolean;
    officer: string | boolean;
    commander: string | boolean;
    titan: string | boolean;
  }[];
}

export const FEATURE_COMPARISON: FeatureCategory[] = [
  {
    name: 'Resources & Limits',
    features: [
      { name: 'Employees', explorer: '5', officer: '20', commander: '50', titan: '100' },
      { name: 'Companies', explorer: '1', officer: '2', commander: '6', titan: '10' },
      { name: 'Industry Verticals', explorer: '1', officer: '2', commander: '5', titan: '10' },
      { name: 'Storage', explorer: '1 GB', officer: '10 GB', commander: '50 GB', titan: '200 GB' },
      { name: 'Email Accounts', explorer: false, officer: '5', commander: '20', titan: '50' },
    ],
  },
  {
    name: 'AI Capabilities',
    features: [
      { name: 'AXOIX AI Chat', explorer: 'Unlimited', officer: 'Unlimited', commander: 'Unlimited', titan: 'Unlimited' },
      { name: 'AI Characters', explorer: '1', officer: '22', commander: '22', titan: '22 + Custom' },
      { name: 'Voice Commands', explorer: false, officer: '81 languages', commander: '81 languages', titan: '81 + Custom' },
      { name: 'AI Costing/week', explorer: '10', officer: '30', commander: '100', titan: '300' },
      { name: 'Chorus Posts/week', explorer: '5', officer: '10+50', commander: '30+150', titan: '100+500' },
      { name: 'Custom AI Training', explorer: false, officer: false, commander: false, titan: true },
    ],
  },
  {
    name: 'Dashboard & UI',
    features: [
      { name: 'Dashboard Access', explorer: 'Basic', officer: 'Full', commander: 'Full', titan: 'Full' },
      { name: 'Storefront Themes', explorer: '1', officer: '3/vertical', commander: 'All', titan: 'All + Custom' },
      { name: 'Analytics', explorer: 'Basic', officer: 'Advanced', commander: 'Advanced', titan: 'Enterprise' },
      // Terms 6a: the internal workspace is unbranded on every plan including
      // Explorer, and the customer-facing "Powered by AXOIX" credit is removable
      // on none. This row previously read explorer/officer/commander false and
      // titan true — wrong in both directions. Tier data agrees: explorer
      // whitelabel=True, the rest unmetered.
      { name: 'Unbranded workspace after sign-in', explorer: true, officer: true, commander: true, titan: true },
    ],
  },
  {
    name: 'Support',
    features: [
      { name: 'Community Support', explorer: true, officer: true, commander: true, titan: true },
      { name: 'Email Support', explorer: false, officer: true, commander: 'Priority', titan: 'Priority' },
      { name: 'Phone Support', explorer: false, officer: false, commander: true, titan: '24/7' },
      { name: 'Dedicated Manager', explorer: false, officer: false, commander: false, titan: true },
    ],
  },
  {
    name: 'API & Integrations',
    features: [
      { name: 'API Access', explorer: false, officer: '5,000/mo', commander: '5,000/mo', titan: 'Unlimited' },
      { name: 'Webhooks', explorer: false, officer: true, commander: true, titan: true },
      { name: 'External APIs (13)', explorer: false, officer: true, commander: true, titan: true },
    ],
  },
  {
    name: 'Specialty Addons',
    features: [
      { name: 'Tender Management', explorer: false, officer: '₹2,500/mo', commander: '₹2,500/mo', titan: 'Included' },
      { name: 'LINK Phone (DID)', explorer: false, officer: '₹2,500/mo', commander: '₹2,500/mo', titan: 'Included' },
      { name: 'Internal PBX (FREE)', explorer: false, officer: true, commander: true, titan: true },
    ],
  },
];

// FAQ data
export interface FAQItem {
  question: string;
  answer: string;
}

export const PRICING_FAQ: FAQItem[] = [
  {
    question: 'What is AXOIX?',
    answer: 'AXOIX is an AI-powered business management suite with 22 intelligent AI characters, voice control in 81 languages, and immersive 3D experiences. It transforms how you manage your business with natural language commands and personalized AI assistance.',
  },
  {
    question: 'Can I try AXOIX for free?',
    answer: 'Yes! The Explorer plan is completely free forever. It gives you access to all 19 verticals and core modules to explore AXOIX. When you\'re ready to unlock the full power of AI-driven business management, you can upgrade to Officer or higher.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit/debit cards, UPI, net banking, and international cards. For Enterprise plans, we also offer invoicing with NET-30 terms.',
  },
  {
    question: 'Can I switch plans later?',
    answer: 'Absolutely! You can upgrade or downgrade your plan at any time. When upgrading, you\'ll get immediate access to new features. When downgrading, changes take effect at the end of your current billing cycle.',
  },
  {
    question: 'What does "81 languages" mean?',
    answer: 'AXOIX supports voice commands and AI conversations in 81 languages including 22 Indian languages, major global languages, and regional dialects. Your AI characters can understand and respond in the language you prefer.',
  },
  {
    question: 'How do the 22 AI characters work?',
    answer: 'Each AI character has a unique personality and specialization. HYPERION leads the team, ECHO handles communications, VARN manages analytics, KEEPER handles HR, FORGE automates workflows, VOYAGE handles tourism, and more. They work together to provide comprehensive business support across 19 industry verticals.',
  },
  {
    // Was: "Commander offers 5-year (₹1,75,999) and 10-year (₹3,00,000)
    // options". Neither product exists. Verified 2026-09-08 against
    // /api/pricing/tiers: commander.fiveYearPrice and .tenYearPrice are both
    // null, and Titan is the ONLY tier carrying long-term pricing. We were
    // advertising two plans nobody could buy, at prices in no table.
    question: 'Are there long-term plans?',
    answer: 'Titan offers 5-year and 10-year plans for organisations committing long term, including all specialty addons and dedicated support. Titan is sales-led — contact us and we will price it for your operation. Officer and Commander are available monthly, quarterly, six-monthly and yearly, with the longer cycles discounted.',
  },
  {
    question: 'Do you offer refunds?',
    answer: 'Yes, we offer a 7-day money-back guarantee for all paid plans. If AXOIX doesn\'t meet your expectations, contact us within 7 days of purchase for a full refund. See our Refund Policy for details.',
  },
  {
    question: 'Is my data secure?',
    answer: 'Every dashboard is protected by 5 layers of defense: iptables firewall (default-deny), CrowdSec threat intelligence (100K+ server blocklists), ModSecurity WAF (1,432 OWASP rules blocking SQLi & XSS), Fail2ban (8 jails), and Nginx rate limiting. Data at rest uses MySQL TDE + AES-encrypted PII fields. SSH is key-only, HSTS preloaded in all browsers, full CSP enforced, and auto-patching enabled.',
  },
  {
    question: 'Can I get a demo before purchasing?',
    answer: 'Of course! You can use the free Explorer plan to explore AXOIX, or contact us to schedule a personalized demo where our team will walk you through all features relevant to your business needs.',
  },
  {
    question: 'What is the Tender Management addon?',
    answer: 'Tender Management (₹2,500/mo) is an optional addon for government contractors and construction companies. It includes NIT Extractor with 67+ field extraction, CPWD DSR rates database, BOQ-based tender quotes, automatic project creation when you win, RA Bills, Measurement Books, and EMD/SD tracking. This is our only paid addon — SEAL (E-Signatures) and REMEDY (Complaints) are FREE for ALL users including the Explorer plan!',
  },
  {
    question: 'What is SEAL and is it really free?',
    answer: 'SEAL (Secure Electronic Agreement & Ledger) is AXOIX\'s built-in e-signature module. It\'s available FREE to ALL users, even on the Explorer (free) plan! Send quotes, contracts, NDAs, and agreements for digital signature without any cost. We believe document signing is a basic business necessity. SEAL is legally compliant under IT Act 2000 with full audit trails.',
  },
  {
    question: 'Is Email Suite included or paid?',
    answer: 'Email Suite is included FREE with all paid plans (Officer and above). You get professional email templates, automated workflows, email tracking, and more at no extra cost. We believe communication tools should be accessible to all businesses.',
  },
  {
    question: 'What is REMEDY and is it really free?',
    answer: 'REMEDY (Resolution & Mediation for Your Disputes) is AXOIX\'s complaint management module. It\'s FREE for ALL users, even on the Explorer (free) plan! REMEDY provides a complete ticket system for customer complaints with SLA monitoring, escalation workflows, priority-based queuing, resolution tracking, and satisfaction surveys. Never let a customer issue fall through the cracks.',
  },
  {
    question: 'Is Tender Management included in Commander plan?',
    answer: 'Yes! The Commander (Lifetime) plan includes Tender Management at no extra cost. This means government contractors get the complete AXOIX experience including NIT extraction, CPWD rates, tender quotes, and auto-project creation - all for a one-time payment.',
  },
  {
    question: 'Do I need the Tender Management addon?',
    answer: 'Only if you\'re a government contractor or work with government tenders. The addon provides specialized tools for NIT document extraction, CPWD rate analysis, tender quoting, and managed project creation. If you don\'t work with government contracts, you don\'t need it - all other features are included in your plan!',
  },
  {
    question: 'What is LINK - Business Phone System?',
    answer: 'LINK is AXOIX\'s built-in VoIP/PBX module. Every paid plan includes FREE internal phone system: internal extensions (3 on Explorer, 10 on Officer, 15 on Commander, unlimited on Titan), browser-based calling (no hardware needed), internal voicemail, and call transfers. If you need real phone numbers to receive external calls, you can upgrade to get a DID number (+91-XXX-XXXX), professional IVR ("Press 1 for Sales..."), call recording, and more. Commander plan includes all LINK features at no extra cost!',
  },
  {
    question: 'Is internal calling really free?',
    answer: 'Yes! With any paid AXOIX plan, you get internal extensions (3 on Explorer, 10 on Officer, 15 on Commander, unlimited on Titan) and browser-based calling at no extra cost. Your team can make calls to each other directly from the dashboard using WebRTC - no phone hardware needed. You only pay extra if you want real phone numbers (DIDs) to receive calls from the public phone network.',
  },
  {
    question: 'What is the WhatsApp CRM Inbox?',
    answer: 'AXOIX includes a multi-channel shared inbox that unifies WhatsApp, Facebook Messenger, Instagram DM, Telegram, SMS, and web chat into one interface. Every conversation gets SLA tracking with 5-level escalation. Features include AI auto-replies with confidence thresholds, round-robin agent assignment, internal notes, tagging, routing rules, and drip sequences for automated follow-ups.',
  },
  {
    question: 'Can I set up automated WhatsApp drip campaigns?',
    answer: 'Yes! Create multi-step WhatsApp sequences with timed delays. For example: welcome message → 24hr delay → product catalog → 48hr delay → special offer. Each step can be conditional based on customer actions. Available on Officer plan and above.',
  },
  {
    question: 'Can I connect my Shopify store?',
    answer: 'Yes! The Shopify connector syncs your products, orders, and customers in real-time. Orders trigger instant WhatsApp notifications. Your Shopify products can display on both your Shopify store and your AXOIX Storefront simultaneously. Multi-store support included — connect unlimited Shopify stores.',
  },
  {
    question: 'Does AXOIX support shipping integrations?',
    answer: 'Yes! The Shiprocket connector provides live shipping rates from 25+ courier partners, automatic AWB generation from orders, real-time shipment tracking, and COD support. Shipping status updates are sent via WhatsApp automatically.',
  },
  {
    question: 'Can IndiaMART leads flow into AXOIX automatically?',
    answer: 'Yes! The IndiaMART connector automatically captures inquiries from your IndiaMART listings into your AXOIX CRM. Leads are deduplicated by phone/email and auto-assigned to sales reps by product category.',
  },
  {
    question: 'Does AXOIX have cart abandonment recovery?',
    answer: 'Yes! Carts abandoned for over 1 hour automatically trigger recovery messages via WhatsApp or email with a summary of items left behind. This runs automatically — no setup required beyond connecting your WhatsApp.',
  },
  {
    question: 'What is CHORUS Studio 2.0?',
    answer: 'CHORUS Studio 2.0 is the AI content pipeline. It generates blog articles, social media posts, email campaigns, podcast episodes, and videos using a multi-agent system. Includes Voice Studio (5 TTS providers including voice cloning), Video Studio (AI video generation), Brand Engine for consistent voice, and a content calendar with distribution to 14 platforms.',
  },
];
